import * as React from 'react';
import { getCurrentStock } from '../../features/StockManagement/Purchases/pdSlice';
import { getItemCategoryList } from '../../features/setup/useableSlice';
import { getAllIHeads } from '../../features/GeneralManagement/itemHeadsSlice';
import { getAllItemCompanies } from '../../features/GeneralManagement/itemCompanySlice';
import { getAllItems } from '../../features/StockManagement/itemsInformationSlice';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../setup/Navbar'
import { Box, Button, Chip, CircularProgress, Container, Dialog, DialogContent, DialogTitle, Drawer, FormControl, Grid2, IconButton, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import StickyHeadTable from '../../BuiltInComponent/TableWithPagination';
import Loading from '../../BuiltInComponent/Loading';
import { printCurrentStockReport, printMinimumStockReport } from '../../utils/currentStockPrint';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import '../../services/GlobalVariables';
export default function CurrentStock() {

    const dispatch = useDispatch();
    const { isLoading, currentStock } = useSelector((state) => state.pd);
    const { user } = useSelector((state) => state.auth);
    const { itemCategories } = useSelector((state) => state.ru);
    const { heads } = useSelector((state) => state.ih);
    const { itemCompanyies } = useSelector((state) => state.itmCmp);
    const { items: itemMaster } = useSelector((state) => state.itemsInf);
    const [stockFilter, setStockFilter] = React.useState('positive');
    const [reportFilters, setReportFilters] = React.useState({ categoryCode: '', headCode: '', itemCompanyCode: '', unit: '', search: '' });
    const [ledgerItem, setLedgerItem] = React.useState(null);
    const [ledgerRows, setLedgerRows] = React.useState([]);
    const [ledgerSummary, setLedgerSummary] = React.useState({ opening: 0, wastage: 0, discard: 0 });
    const [reconciliationOpen, setReconciliationOpen] = React.useState(false);
    const [reconciliationRows, setReconciliationRows] = React.useState([]);
    const [reconciliationLoading, setReconciliationLoading] = React.useState(false);
    const [ledgerLoading, setLedgerLoading] = React.useState(false);
    const [ledgerError, setLedgerError] = React.useState('');
    const [ledgerFilter, setLedgerFilter] = React.useState({ fromDate: '', toDate: '', movementType: '' });
    const formatLedgerDate = (value) => value && dayjs(value).isValid() ? dayjs(value).format('DD-MMM-YYYY hh:mm A') : '-';

    React.useEffect(() => {
        dispatch(getCurrentStock());
        dispatch(getItemCategoryList());
        dispatch(getAllIHeads());
        dispatch(getAllItemCompanies());
        dispatch(getAllItems());
    }, [dispatch]);

    const stockRows = React.useMemo(() => {
        const itemByCode = new Map((itemMaster || []).map((item) => [String(item.itemCode), item]));
        return (currentStock || []).map((row) => {
            const item = itemByCode.get(String(row.itemCode)) || {};
            return { ...item, ...row, categoryCode: row.categoryCode || item.categoryCode, headCode: row.headCode || item.headCode, itmCmpCode: row.itmCmpCode || item.itmCmpCode, categoryName: row.categoryName || item.cateDescription || '', headName: row.headName || item.headDescription || '', itemCompanyName: row.itemCompanyName || item.itmCmpDescription || '' };
        });
    }, [currentStock, itemMaster]);

    const filteredStock = React.useMemo(() => {
        const rows = stockRows.map((row) => ({
            ...row,
            amount: Number(row.currStock || 0) * Number(row.itemRate || 0)
        }));
        if (stockFilter === 'positive') return rows.filter((row) => Number(row.currStock || 0) > 0);
        if (stockFilter === 'zero') return rows.filter((row) => Number(row.currStock || 0) === 0);
        return rows;
    }, [stockRows, stockFilter]);

    const filteredReportStock = React.useMemo(() => {
        const search = String(reportFilters.search || '').trim().toLowerCase();
        const categoryName = (itemCategories || []).find((row) => String(row.cateCode) === String(reportFilters.categoryCode))?.cateDescription;
        const headName = (heads || []).find((row) => String(row.headCode) === String(reportFilters.headCode))?.headDescription;
        const companyName = (itemCompanyies || []).find((row) => String(row.itmCmpCode) === String(reportFilters.itemCompanyCode))?.itmCmpDescription;
        return filteredStock.filter((row) => {
            if (reportFilters.categoryCode && String(row.categoryCode || '') !== String(reportFilters.categoryCode) && String(row.categoryName || '').trim().toLowerCase() !== String(categoryName || '').trim().toLowerCase()) return false;
            if (reportFilters.headCode && String(row.headCode || '') !== String(reportFilters.headCode) && String(row.headName || '').trim().toLowerCase() !== String(headName || '').trim().toLowerCase()) return false;
            if (reportFilters.itemCompanyCode && String(row.itmCmpCode || '') !== String(reportFilters.itemCompanyCode) && String(row.itemCompanyName || '').trim().toLowerCase() !== String(companyName || '').trim().toLowerCase()) return false;
            if (reportFilters.unit && String(row.inUnit || '') !== reportFilters.unit) return false;
            return !search || [row.itemCode, row.itemDesc, row.categoryName, row.headName, row.itemCompanyName].some((value) => String(value || '').toLowerCase().includes(search));
        });
    }, [filteredStock, reportFilters]);

    const grandTotal = React.useMemo(
        () => filteredReportStock.reduce((total, row) => total + Number(row.amount || 0), 0),
        [filteredReportStock]
    );
    const formatAmount = (value) => Number(value || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const units = React.useMemo(() => [...new Set((currentStock || []).map((row) => String(row.inUnit || '').trim()).filter(Boolean))].sort(), [currentStock]);
    const updateReportFilter = (name, value) => setReportFilters((old) => ({ ...old, [name]: value }));
    const clearReportFilters = () => setReportFilters({ categoryCode: '', headCode: '', itemCompanyCode: '', unit: '', search: '' });

    const minimumStockItems = React.useMemo(() => (currentStock || []).filter((row) => {
        const minimum = Number(row.minQuantity || 0);
        return minimum > 0 && Number(row.currStock || 0) < minimum;
    }), [currentStock]);

    const loadItemLedger = async (item, filters) => {
        if (!item) return;
        const activeFilters = filters || ledgerFilter;
        setLedgerLoading(true);
        setLedgerError('');
        try {
            const response = await axios.get(global.SET_API_URL + 'production/ledger', { params: {
                itemCode: item.itemCode,
                fromDate: activeFilters.fromDate || undefined,
                toDate: activeFilters.toDate || undefined,
                movementType: activeFilters.movementType || undefined
            }});
            setLedgerRows(Array.isArray(response.data) ? response.data : response.data?.rows || []);
            setLedgerSummary(response.data?.summary || { opening: 0, wastage: 0, discard: 0 });
        } catch (error) {
            setLedgerRows([]);
            setLedgerError(error.response?.data?.message || error.message || 'Unable to load item ledger');
        } finally {
            setLedgerLoading(false);
        }
    };

    const onLedgerClick = (itemCode) => {
        const item = (currentStock || []).find((row) => String(row.itemCode) === String(itemCode));
        if (!item) return;
        const emptyFilters = { fromDate: '', toDate: '', movementType: '' };
        setLedgerItem(item);
        setLedgerFilter(emptyFilters);
        loadItemLedger(item, emptyFilters);
    };

    const closeLedger = () => {
        setLedgerItem(null);
        setLedgerRows([]);
        setLedgerError('');
    };

    const isOutMovement = (type = '') => type.endsWith('_out') || type === 'wastage' || type === 'discard' || type === 'manual';
    const movementLabel = (type = '') => type === 'manual' ? 'Manual Issuance' : type.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    const formatQuantity = (value) => Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 3 });
    const summaryCards = [
        { label: 'Opening', value: ledgerSummary.opening },
        { label: 'Wastage', value: ledgerSummary.wastage },
        { label: 'Discard', value: ledgerSummary.discard }
    ];

    const onNullCurrentStock = async () => {
        if (!window.confirm('Set current stock of ALL items to zero? A balancing stock transaction will be posted.')) return;
        if (!window.confirm('Final confirmation: this action cannot be undone. Continue?')) return;
        try {
            const response = await axios.post(global.SET_API_URL + 'pd/nullCurrentStock', { userID: user?.userID });
            window.alert(response.data.message);
            await dispatch(getCurrentStock());
        } catch (error) {
            window.alert(error.response?.data?.message || error.message || 'Unable to null current stock');
        }
    };

    const openReconciliation = async () => {
        setReconciliationOpen(true);
        setReconciliationLoading(true);
        try {
            const response = await axios.get(global.SET_API_URL + 'pd/stockReconciliation');
            setReconciliationRows((response.data || []).map((row) => {
                const adjustment = Number(row.adjustment || 0);
                const grn = Number(row.grn || 0); const issuance = Number(row.issuance || 0);
                const wastage = Number(row.wastage || 0); const discard = Number(row.discard || 0);
                const opening = Number(row.openingStock || 0);
                const closingStock = opening + adjustment + grn - issuance - wastage - discard;
                const physicalStock = closingStock; const itemRate = Number(row.itemRate || 0);
                return { ...row, closingStock, physicalStock, variance: 0,
                    closingAmount: closingStock * itemRate, physicalAmount: closingStock * itemRate,
                    varianceAmount: 0 };
            }));
        } catch (error) {
            setReconciliationRows([]);
            window.alert(error.response?.data?.message || 'Unable to load stock reconciliation report');
        } finally {
            setReconciliationLoading(false);
        }
    };
    const updatePhysicalStock = (itemCode, value) => {
        setReconciliationRows((rows) => rows.map((row) => {
            if (String(row.itemCode) !== String(itemCode)) return row;
            const physicalStock = value === '' ? '' : Number(value);
            const variance = Number(row.closingStock || 0) - Number(physicalStock || 0);
            return {
                ...row, physicalStock, variance,
                physicalAmount: Number(physicalStock || 0) * Number(row.itemRate || 0),
                varianceAmount: variance * Number(row.itemRate || 0)
            };
        }));
    };
    const getReconciliationExportRows = () => {
        return reconciliationRows.map((row) => ({
            'Item Code': row.itemCode,
            'Item Description': row.itemDesc,
            Opening: Number(row.openingStock || 0),
            Adjustment: Number(row.adjustment || 0),
            GRN: Number(row.grn || 0),
            Issuance: Number(row.issuance || 0),
            Wastage: Number(row.wastage || 0),
            Discard: Number(row.discard || 0),
            Closing: Number(row.closingStock || 0),
            'Physical Stock': Number(row.physicalStock || 0),
            Variance: Number(row.variance || 0),
            Rate: Number(row.itemRate || 0),
            'Variance Amount': Number(row.varianceAmount || 0)
        }));
    };
    const exportReconciliationExcel = () => {
        const rows = getReconciliationExportRows();
        const sheet = XLSX.utils.json_to_sheet(rows);
        sheet['!cols'] = [{ wch: 12 }, { wch: 32 }, ...Array(11).fill({ wch: 14 })];
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, 'Stock Reconciliation');
        XLSX.writeFile(book, `Stock_Reconciliation_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };
    const exportReconciliationPdf = () => {
        const rows = getReconciliationExportRows();
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
        doc.setFontSize(15);
        doc.text('Stock Reconciliation Report', 14, 13);
        autoTable(doc, {
            startY: 18,
            head: [Object.keys(rows[0] || getReconciliationExportRows()[0] || {})],
            body: rows.map((row) => Object.values(row)),
            styles: { fontSize: 7, cellPadding: 1.4 },
            headStyles: { fillColor: [139, 0, 0] },
            margin: { left: 8, right: 8 }
        });
        doc.save(`Stock_Reconciliation_${new Date().toISOString().slice(0, 10)}.pdf`);
    };
    const printReconciliation = () => {
        const printWindow = window.open('', '_blank', 'width=1400,height=900');
        if (!printWindow) return window.alert('Please allow pop-ups to print the report.');
        const rows = getReconciliationExportRows();
        const headers = ['Item Code', 'Item Description', 'Opening', 'Adjustment', 'GRN', 'Issuance', 'Wastage', 'Discard', 'Closing', 'Physical Stock', 'Variance', 'Rate', 'Variance Amount'];
        const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char]));
        const body = rows.map((row) => `<tr>${headers.map((header, index) => `<td class="${index > 1 ? 'number' : ''}">${escapeHtml(row[header])}</td>`).join('')}</tr>`).join('');
        printWindow.document.write(`<!doctype html><html><head><title>Stock Reconciliation Report</title><style>@page{size:A3 landscape;margin:8mm}body{font:10px Arial;color:#222}h1{font-size:18px}p{color:#555}table{width:100%;border-collapse:collapse}th{background:#8b0000;color:#fff;padding:5px}td{padding:4px;border-bottom:1px solid #ddd}.number{text-align:right}</style></head><body><h1>Stock Reconciliation Report</h1><p>Opening + Adjustment + GRN - Issuance - Wastage - Discard = Closing; Closing - Physical Stock = Variance</p><table><thead><tr>${headers.map((header) => `<th>${header}</th>`).join('')}</tr></thead><tbody>${body}</tbody></table><script>window.onload=()=>{window.focus();window.print();};</script></body></html>`);
        printWindow.document.close();
    };


    const getCurrentStockExportRows = () => filteredReportStock.map((row) => ({
        'Item Code': row.itemCode || '',
        'Item Description': row.itemDesc || '',
        Category: row.categoryName || '',
        Head: row.headName || '',
        Company: row.itemCompanyName || '',
        Unit: row.inUnit || '',
        'Current Stock': Number(row.currStock || 0),
        'Current Rate': Number(row.itemRate || 0),
        Amount: Number(row.amount || 0)
    }));

    const exportCurrentStockExcel = () => {
        const rows = getCurrentStockExportRows();
        if (!rows.length) return;
        const headers = Object.keys(rows[0]);
        const sheet = XLSX.utils.aoa_to_sheet([
            [user?.cmpName || user?.company?.cmpName || 'Lucky Broast'],
            ['Current Stock Report'],
            ['Filter', stockFilter === 'positive' ? 'Greater than zero' : (stockFilter === 'zero' ? 'Only zero' : 'All stock')],
            ['Grand Total', grandTotal],
            ['Generated', new Date().toLocaleString('en-GB')],
            [],
            headers,
            ...rows.map((row) => headers.map((header) => row[header]))
        ]);
        sheet['!cols'] = [{ wch: 16 }, { wch: 36 }, { wch: 16 }, { wch: 16 }, { wch: 18 }];
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, 'Current Stock');
        XLSX.writeFile(book, 'Current_Stock_Report_' + new Date().toISOString().slice(0, 10) + '.xlsx');
    };

    const exportCurrentStockPdf = () => {
        const rows = getCurrentStockExportRows();
        if (!rows.length) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        doc.setFontSize(15);
        doc.text(user?.cmpName || user?.company?.cmpName || 'Lucky Broast', 14, 13);
        doc.setFontSize(12);
        doc.text('Current Stock Report', 14, 20);
        doc.setFontSize(8);
        doc.text('Filter: ' + (stockFilter === 'positive' ? 'Greater than zero' : (stockFilter === 'zero' ? 'Only zero' : 'All stock')) + '   |   Generated: ' + new Date().toLocaleString('en-GB'), 14, 26);
        doc.setFontSize(10);
        doc.text('Grand Total: Rs. ' + formatAmount(grandTotal), 14, 32);
        autoTable(doc, {
            startY: 36,
            head: [Object.keys(rows[0])],
            body: rows.map((row) => Object.values(row)),
            theme: 'grid',
            headStyles: { fillColor: [139, 0, 0] },
            styles: { fontSize: 8, cellPadding: 1.8 },
            columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } }
        });
        doc.save('Current_Stock_Report_' + new Date().toISOString().slice(0, 10) + '.pdf');
    };
    const tableColumns = [
        { fieldName: 'itemCode', cName: 'Item Code', textAlign: 'left', width: 80, status: 'en', dType: 'string' },
        { fieldName: 'itemDesc', cName: 'Item Description', textAlign: 'left', width: 80, status: 'en', dType: 'string' },
        { fieldName: 'categoryName', cName: 'Category', textAlign: 'left', width: 100, status: 'en', dType: 'string' },
        { fieldName: 'headName', cName: 'Head', textAlign: 'left', width: 100, status: 'en', dType: 'string' },
        { fieldName: 'itemCompanyName', cName: 'Company', textAlign: 'left', width: 100, status: 'en', dType: 'string' },
        { fieldName: 'inUnit', cName: 'Unit', textAlign: 'left', width: 65, status: 'en', dType: 'string' },
        { fieldName: 'currStock', cName: 'Current Stock', textAlign: 'right', width: 80, status: 'en', dType: 'number' },
        { fieldName: 'itemRate', cName: 'Current Rate', textAlign: 'right', width: 90, status: 'en', dType: 'number' },
        { fieldName: 'amount', cName: 'Amount', textAlign: 'right', width: 100, status: 'en', dType: 'number' },
        { fieldName: 'Edit', cName: 'Edit', textAlign: 'right', width: 80, status: 'dis', dType: 'string' },
        { fieldName: 'Delete', cName: 'Delete', textAlign: 'right', width: 80, status: 'dis', dType: 'string' },
        { fieldName: 'Action', cName: 'Ledger', textAlign: 'center', width: 90, status: 'en', buttonText: 'Ledger' },
    ];

    const minimumStockColumns = [
        { fieldName: 'itemCode', cName: 'Item Code', textAlign: 'left', width: 80, status: 'en', dType: 'string' },
        { fieldName: 'itemDesc', cName: 'Item Description', textAlign: 'left', width: 160, status: 'en', dType: 'string' },
        { fieldName: 'minQuantity', cName: 'Minimum Stock', textAlign: 'right', width: 90, status: 'en', dType: 'number' },
        { fieldName: 'currStock', cName: 'Current Stock', textAlign: 'right', width: 90, status: 'en', dType: 'number' },
        { fieldName: 'Edit', cName: 'Edit', status: 'dis' },
        { fieldName: 'Delete', cName: 'Delete', status: 'dis' },
        { fieldName: 'Action', cName: 'Ledger', textAlign: 'center', width: 90, status: 'en', buttonText: 'Ledger' },
    ];

    return (
        <>
            <Navbar />
            <Container>
                <Grid2 container spacing={1.5} justifyContent="flex-end" alignItems="center" sx={{ my: 2 }}>
                    <Grid2 size={{ xs: 12, sm: 'auto' }}>
                        <Button fullWidth variant="outlined" color="error" onClick={onNullCurrentStock}>
                            Null Current Stock
                        </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 'auto' }}>
                        <Button fullWidth variant="outlined" onClick={openReconciliation}>
                            Stock Reconciliation
                        </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 'auto' }}>
                        <Button fullWidth variant="contained" startIcon={<PrintIcon />}
                            onClick={() => printCurrentStockReport(filteredReportStock, stockFilter, user)}>
                            Print Report
                        </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 'auto' }}>
                        <Button fullWidth variant="outlined" onClick={exportCurrentStockPdf} disabled={!filteredReportStock.length}>
                            Export PDF
                        </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 'auto' }}>
                        <Button fullWidth variant="contained" color="success" onClick={exportCurrentStockExcel} disabled={!filteredReportStock.length}>
                            Export Excel
                        </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 5, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="stock-filter-label">Stock Filter</InputLabel>
                            <Select labelId="stock-filter-label" value={stockFilter} label="Stock Filter"
                                onChange={(event) => setStockFilter(event.target.value)}>
                                <MenuItem value="positive">Greater than zero</MenuItem>
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="zero">Only zero</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid2>
                </Grid2>
                <Paper variant='outlined' sx={{ p: 1.5, mb: 2 }}><Grid2 container spacing={1.5} alignItems='center'>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}><TextField fullWidth size='small' label='Search item' value={reportFilters.search} onChange={(event) => updateReportFilter('search', event.target.value)} /></Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2.25 }}><FormControl fullWidth size='small'><InputLabel>Category</InputLabel><Select value={reportFilters.categoryCode} label='Category' onChange={(event) => updateReportFilter('categoryCode', event.target.value)}><MenuItem value=''>All Categories</MenuItem>{(itemCategories || []).map((row) => <MenuItem key={row.cateCode} value={row.cateCode}>{row.cateDescription}</MenuItem>)}</Select></FormControl></Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2.25 }}><FormControl fullWidth size='small'><InputLabel>Head</InputLabel><Select value={reportFilters.headCode} label='Head' onChange={(event) => updateReportFilter('headCode', event.target.value)}><MenuItem value=''>All Heads</MenuItem>{(heads || []).map((row) => <MenuItem key={row.headCode} value={row.headCode}>{row.headDescription}</MenuItem>)}</Select></FormControl></Grid2>
                    <Grid2 size={{ xs: 12, sm: 6, md: 2.25 }}><FormControl fullWidth size='small'><InputLabel>Company</InputLabel><Select value={reportFilters.itemCompanyCode} label='Company' onChange={(event) => updateReportFilter('itemCompanyCode', event.target.value)}><MenuItem value=''>All Companies</MenuItem>{(itemCompanyies || []).map((row) => <MenuItem key={row.itmCmpCode} value={row.itmCmpCode}>{row.itmCmpDescription}</MenuItem>)}</Select></FormControl></Grid2>
                    <Grid2 size={{ xs: 8, sm: 6, md: 1.5 }}><FormControl fullWidth size='small'><InputLabel>Unit</InputLabel><Select value={reportFilters.unit} label='Unit' onChange={(event) => updateReportFilter('unit', event.target.value)}><MenuItem value=''>All Units</MenuItem>{units.map((unit) => <MenuItem key={unit} value={unit}>{unit}</MenuItem>)}</Select></FormControl></Grid2>
                    <Grid2 size={{ xs: 4, sm: 6, md: 0.75 }}><Button fullWidth size='small' onClick={clearReportFilters}>Clear</Button></Grid2>
                </Grid2></Paper>
                <Paper variant='outlined' sx={{ p: 1.5, mb: 2, bgcolor: '#fff8df', borderColor: '#d4a017' }}>
                    <Typography variant='body2' color='text.secondary'>Grand Total</Typography>
                    <Typography variant='h5' fontWeight={700} color='success.dark'>Rs. {formatAmount(grandTotal)}</Typography>
                </Paper>
                {!isLoading && minimumStockItems.length > 0 && <Grid2 sx={{ mb: 3 }}>
                    <Grid2 container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6" color="error.main" fontWeight="bold">
                            Minimum Stock Alert ({minimumStockItems.length})
                        </Typography>
                        <Button variant="contained" color="error" startIcon={<PrintIcon />}
                            onClick={() => printMinimumStockReport(minimumStockItems, user)}>
                            Print Minimum Stock
                        </Button>
                    </Grid2>
                    <StickyHeadTable tableColumns={minimumStockColumns} tableData={minimumStockItems} perPage={25}
                        onButtonClick={onLedgerClick} />
                </Grid2>}
                {isLoading ? <Loading /> : <StickyHeadTable tableColumns={tableColumns} tableData={filteredReportStock} perPage={100}
                    onButtonClick={onLedgerClick} />
                }

            </Container>
            <Drawer anchor={'right'} open={Boolean(ledgerItem) || reconciliationOpen} onClose={reconciliationOpen ? () => setReconciliationOpen(false) : closeLedger} PaperProps={{ sx: { width: { xs: '100%', md: '70%' }, maxWidth: 1050 } }}>
            <Dialog open={reconciliationOpen} onClose={() => setReconciliationOpen(false)} maxWidth="xl" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6">Stock Reconciliation Report</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button size="small" variant="outlined" onClick={printReconciliation} disabled={!reconciliationRows.length}>Print</Button>
                            <Button size="small" variant="outlined" onClick={exportReconciliationPdf} disabled={!reconciliationRows.length}>Export PDF</Button>
                            <Button size="small" variant="contained" onClick={exportReconciliationExcel} disabled={!reconciliationRows.length}>Export Excel</Button>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Opening + Adjustment + GRN - Issuance - Wastage - Discard = Closing; Closing - Physical Stock = Variance. Amounts use the current item rate.</Typography>
                    {reconciliationLoading ? <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box> : <TableContainer sx={{ maxHeight: '70vh' }}><Table stickyHeader size="small">
                        <TableHead><TableRow>{['Item', 'Opening', 'Adjustment', 'GRN', 'Issuance', 'Wastage', 'Discard', 'Closing', 'Physical', 'Variance', 'Rate', 'Variance Amount'].map((heading, index) => <TableCell key={heading} align={index ? 'right' : 'left'} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{heading}</TableCell>)}</TableRow></TableHead>
                        <TableBody>{reconciliationRows.map((row) => <TableRow key={row.itemCode} hover>
                            <TableCell sx={{ minWidth: 190 }}>{row.itemCode} - {row.itemDesc}</TableCell>
                            {[row.openingStock, row.adjustment, row.grn, row.issuance, row.wastage, row.discard, row.closingStock].map((value, index) => <TableCell key={index} align="right">{formatQuantity(value)}</TableCell>)}
                            <TableCell align="right"><TextField type="number" variant="standard" value={row.physicalStock} onChange={(event) => updatePhysicalStock(row.itemCode, event.target.value)} inputProps={{ step: '0.001', style: { textAlign: 'right', width: 75 } }} /></TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatQuantity(row.variance)}</TableCell>
                            <TableCell align="right">{Number(row.itemRate || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: row.varianceAmount < 0 ? 'error.main' : row.varianceAmount > 0 ? 'success.main' : 'inherit' }}>{Number(row.varianceAmount || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                        </TableRow>)}{reconciliationRows.length === 0 && <TableRow><TableCell colSpan={12} align="center" sx={{ py: 6 }}>No reconciliation records found</TableCell></TableRow>}</TableBody>
                    </Table></TableContainer>}
                </DialogContent>
            </Dialog>
                <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box><Typography variant={'h5'} fontWeight={700}><HistoryIcon sx={{ verticalAlign: 'middle', mr: 1 }} />Item Wise Ledger</Typography><Typography color={'text.secondary'}>{ledgerItem?.itemCode} — {ledgerItem?.itemDesc}</Typography></Box>
                        <Box><Chip label={'Current Stock: ' + formatQuantity(ledgerItem?.currStock)} color={'success'} /><Tooltip title={'Close'}><IconButton onClick={closeLedger}><CloseIcon /></IconButton></Tooltip></Box>
                    </Box>
                    <Grid2 container spacing={1.5} sx={{ mb: 2 }}>
                        {summaryCards.map(card => <Grid2 key={card.label} size={{ xs: 12, sm: 4 }}>
                            <Paper variant='outlined' sx={{ p: 1.5, bgcolor: '#fff' }}>
                                <Typography variant='caption' color='text.secondary'>{card.label}</Typography>
                                <Typography variant='h6' fontWeight={700}>{formatQuantity(card.value)}</Typography>
                            </Paper>
                        </Grid2>)}
                    </Grid2>
                    <Paper variant='outlined' sx={{ p: 2, mb: 2 }}><Grid2 container spacing={1.5}>
                        <Grid2 size={{ xs: 12, sm: 4 }}><TextField fullWidth size='small' type='date' label='From Date' value={ledgerFilter.fromDate} InputLabelProps={{ shrink: true }} onChange={(e) => setLedgerFilter((old) => ({ ...old, fromDate: e.target.value }))} /></Grid2>
                        <Grid2 size={{ xs: 12, sm: 4 }}><TextField fullWidth size='small' type='date' label='To Date' value={ledgerFilter.toDate} InputLabelProps={{ shrink: true }} onChange={(e) => setLedgerFilter((old) => ({ ...old, toDate: e.target.value }))} /></Grid2>
                        <Grid2 size={{ xs: 12, sm: 4 }}><FormControl fullWidth size='small'><InputLabel>Movement Type</InputLabel><Select value={ledgerFilter.movementType} label='Movement Type' onChange={(e) => setLedgerFilter((old) => ({ ...old, movementType: e.target.value }))}><MenuItem value=''>All</MenuItem>{['purchase_in','manual','wastage','discard','production_in','production_out','sale_out','adjustment'].map((type) => <MenuItem key={type} value={type}>{movementLabel(type)}</MenuItem>)}</Select></FormControl></Grid2>
                        <Grid2 size={{ xs: 12 }} sx={{ textAlign: 'right' }}><Button variant='contained' onClick={() => loadItemLedger(ledgerItem)}>Apply Filter</Button></Grid2>
                    </Grid2></Paper>
                    <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
                        {ledgerLoading ? <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box> : ledgerError ? <Typography color='error' sx={{ p: 4, textAlign: 'center' }}>{ledgerError}</Typography> :
                            <TableContainer sx={{ maxHeight: 'calc(100vh - 285px)' }}><Table stickyHeader size='small'>
                                <TableHead><TableRow>{['Date & Time','Transaction','Reference','In','Out','Balance','Remarks'].map((heading, index) => <TableCell key={heading} align={index >= 3 && index <= 5 ? 'right' : 'left'} sx={{ fontWeight: 700 }}>{heading}</TableCell>)}</TableRow></TableHead>
                                <TableBody>{ledgerRows.map((row) => { const out = isOutMovement(row.movementType); return <TableRow key={row.ledgerID} hover>
                                    <TableCell>{formatLedgerDate(row.recordedAt)}</TableCell><TableCell>{movementLabel(row.movementType)}</TableCell><TableCell>{row.referenceType}{row.referenceID ? ' #' + row.referenceID : ''}</TableCell>
                                    <TableCell align='right' sx={{ color: 'success.main' }}>{out ? '—' : formatQuantity(row.quantity)}</TableCell><TableCell align='right' sx={{ color: 'error.main' }}>{out ? formatQuantity(row.quantity) : '—'}</TableCell><TableCell align='right' sx={{ fontWeight: 700 }}>{formatQuantity(row.balanceAfter)}</TableCell><TableCell>{row.remarks || '—'}</TableCell>
                                </TableRow>; })}{ledgerRows.length === 0 && <TableRow><TableCell colSpan={7} align='center' sx={{ py: 6 }}>No ledger entries found</TableCell></TableRow>}</TableBody>
                            </Table></TableContainer>}
                    </Paper>
                </Box>
            </Drawer>
        </>
    );
}




